import { homeCurrencyLabel } from '@/lib/org-locale';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  AddCurrencyDialog,
  ImportRatesDialog,
  RateDialog,
} from '@/components/currencies/currency-dialogs';
import { RevalueDialog } from '@/components/currencies/revalue-dialog';
import { ErrorBanner, Field, NativeSelect, Textarea } from '@/components/form';
import { TextLink } from '@/components/menu-link';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError, api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import {
  type CurrenciesOverview,
  type ExchangeRate,
  rateLine,
  type Revaluation,
  sourceLabel,
  useCurrencies,
  useExchangeRates,
  useHomeCurrency,
  useRevaluations,
} from '@/lib/currencies';
import { todayIso } from '@/lib/dates';
import { formatAmount, formatDate, formatDateTime } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import { cn } from '@/lib/utils';
import { ltr } from '@/lib/arabic';
import { useT } from '@/lib/i18n';

type Tab = 'currencies' | 'rates' | 'revaluations';

export const Route = createFileRoute('/settings/currencies')({
  component: CurrenciesPage,
  validateSearch: (s: Record<string, unknown>): { tab?: Tab } =>
    s['tab'] === 'rates' || s['tab'] === 'revaluations' ? { tab: s['tab'] } : {},
});

const TABS: Array<[Tab, 'tabCurrencies' | 'tabRates' | 'tabRevaluations']> = [
  ['currencies', 'tabCurrencies'],
  ['rates', 'tabRates'],
  ['revaluations', 'tabRevaluations'],
];

/**
 * Currencies (document 06 G10; QBO gear › Lists › Currencies): the company's currencies with
 * their latest rates, the rate history (typed, imported or fetched from the rate provider) and
 * Revalue currency with its history (A13).
 */
function CurrenciesPage() {
  const t = useT();
  const orgId = useOrgId();
  const { tab = 'currencies' } = Route.useSearch();
  const me = useMe();
  const staff = me.data?.isStaff ?? false;
  const overview = useCurrencies();
  const homeFallback = useHomeCurrency();
  const [adding, setAdding] = useState(false);
  const [rateFor, setRateFor] = useState<{ currency: string; date?: string; rate?: string } | null>(
    null,
  );
  const [newRate, setNewRate] = useState(false);
  const [importing, setImporting] = useState(false);
  const [revalue, setRevalue] = useState<string | null | false>(false);
  const qc = useQueryClient();
  const getRates = useMutation({
    mutationFn: () =>
      api<ExchangeRate[]>(`/orgs/${orgId}/exchange-rates/fetch`, {
        method: 'POST',
        body: JSON.stringify({ date: todayIso() }),
      }),
    onSuccess: async (rows) => {
      await qc.invalidateQueries({ queryKey: ['currencies', orgId] });
      toast(
        t('settingsPages.general.currencies.todaysRates', {
          rates: ltr(rows.map((r) => `${r.currency} ${r.rate}`).join(', ')),
        }),
      );
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : String(e), { tone: 'error' }),
  });
  const remove = useMutation({
    mutationFn: (code: string) =>
      api<CurrenciesOverview>(`/orgs/${orgId}/currencies/${code}`, { method: 'DELETE' }),
    onSuccess: async (_r, code) => {
      await qc.invalidateQueries({ queryKey: ['currencies', orgId] });
      toast(t('settingsPages.general.currencies.removed', { code: ltr(code) }));
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : String(e), { tone: 'error' }),
  });

  if (!orgId)
    return <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />;
  const o = overview.data;
  const codes = o?.currencies.map((c) => c.code) ?? [];
  const home = o?.homeCurrency ?? homeFallback;

  return (
    <>
      <PageHeader
        title={t('settingsPages.general.currencies.title')}
        description={t('settingsPages.general.currencies.description', { home: ltr(home) })}
        actions={
          staff && o?.enabled ? (
            <>
              {o.rateProvider ? (
                <Button
                  variant="outline"
                  disabled={!codes.length || getRates.isPending}
                  onClick={() => getRates.mutate()}
                >
                  {getRates.isPending
                    ? t('settingsPages.general.currencies.gettingRates')
                    : t('settingsPages.general.currencies.getTodaysRates')}
                </Button>
              ) : null}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-1" disabled={!codes.length}>
                    {t('settingsPages.general.currencies.more')} <ChevronDown className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => setNewRate(true)}>
                    {t('settingsPages.general.currencies.enterRate')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setImporting(true)}>
                    {t('settingsPages.general.currencies.importRates')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setRevalue(null)}>
                    {t('settingsPages.general.currencies.revalueCurrency')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button disabled={!o.available.length} onClick={() => setAdding(true)}>
                {t('settingsPages.general.currencies.addCurrency')}
              </Button>
            </>
          ) : null
        }
      />
      {overview.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : overview.isError ? (
        <EmptyState
          title={t('settingsPages.general.currencies.loadError')}
          hint={overview.error.message}
        />
      ) : !o!.enabled ? (
        <EmptyState
          title={t('settingsPages.general.currencies.offTitle')}
          hint={t('settingsPages.general.currencies.offHint')}
          action={
            <Button asChild variant="outline">
              <Link to="/settings/company" search={{ tab: 'advanced' }}>
                {t('settingsPages.general.currencies.companySettings')}
              </Link>
            </Button>
          }
        />
      ) : (
        <>
          <div className="mb-3 flex gap-6 border-b" role="tablist">
            {TABS.map(([key, text]) => (
              <Link
                key={key}
                role="tab"
                aria-selected={tab === key}
                to="/settings/currencies"
                search={key === 'currencies' ? {} : { tab: key }}
                className={cn(
                  '-mb-px border-b-2 px-1 pb-2 text-sm',
                  tab === key
                    ? 'border-primary font-medium text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {t(`settingsPages.general.currencies.${text}`)}
              </Link>
            ))}
          </div>
          {tab === 'currencies' ? (
            <CurrencyList
              o={o!}
              staff={staff}
              onRate={(currency) => setRateFor({ currency })}
              onRevalue={(c) => setRevalue(c)}
              onRemove={(c) => {
                if (
                  window.confirm(
                    t('settingsPages.general.currencies.removeConfirm', { code: ltr(c) }),
                  )
                )
                  remove.mutate(c);
              }}
            />
          ) : tab === 'rates' ? (
            <RateHistory
              codes={codes}
              home={home}
              staff={staff}
              onEdit={(r) => setRateFor({ currency: r.currency, date: r.date, rate: r.rate })}
            />
          ) : (
            <RevaluationHistory home={home} staff={staff} />
          )}
        </>
      )}
      {o ? (
        <>
          <AddCurrencyDialog
            open={adding}
            onClose={() => setAdding(false)}
            available={o.available}
          />
          <RateDialog
            open={Boolean(rateFor) || newRate}
            onClose={() => {
              setRateFor(null);
              setNewRate(false);
            }}
            currencies={codes}
            home={home}
            initial={rateFor}
          />
          <ImportRatesDialog open={importing} onClose={() => setImporting(false)} />
          <RevalueDialog
            open={revalue !== false}
            onClose={() => setRevalue(false)}
            currencies={codes}
            home={home}
            initialCurrency={revalue || null}
          />
        </>
      ) : null}
    </>
  );
}

function CurrencyList({
  o,
  staff,
  onRate,
  onRevalue,
  onRemove,
}: {
  o: CurrenciesOverview;
  staff: boolean;
  onRate: (currency: string) => void;
  onRevalue: (currency: string) => void;
  onRemove: (currency: string) => void;
}) {
  const t = useT();
  if (!o.currencies.length)
    return (
      <EmptyState
        title={t('settingsPages.general.currencies.emptyTitle')}
        hint={t('settingsPages.general.currencies.emptyHint')}
      />
    );
  return (
    <div className="rounded-lg border bg-card">
      <Table aria-label={t('settingsPages.general.currencies.tabCurrencies')}>
        <TableHeader>
          <TableRow>
            <TableHead>{t('settingsPages.general.currencies.colCurrency')}</TableHead>
            <TableHead className="text-end">
              {t('settingsPages.general.currencies.colExchangeRate')}
            </TableHead>
            <TableHead className="w-[140px]">
              {t('settingsPages.general.currencies.colAsOf')}
            </TableHead>
            <TableHead className="w-[160px] text-end">
              {t('settingsPages.general.currencies.colAction')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell>
              {homeCurrencyLabel(o.homeCurrency)}{' '}
              <span className="text-xs text-muted-foreground">
                {t('settingsPages.general.currencies.homeCurrencyTag')}
              </span>
            </TableCell>
            <TableCell className="tnum text-end">1</TableCell>
            <TableCell />
            <TableCell />
          </TableRow>
          {o.currencies.map((c) => (
            <TableRow key={c.code} data-testid="currency-row">
              <TableCell>
                {c.code} – {c.name}
              </TableCell>
              <TableCell className="tnum text-end">
                {c.latestRate
                  ? rateLine(c.code, c.latestRate, o.homeCurrency)
                  : t('settingsPages.general.currencies.noRateYet')}
              </TableCell>
              <TableCell>{c.latestRateDate ? formatDate(c.latestRateDate) : ''}</TableCell>
              <TableCell className="text-end">
                {staff ? (
                  <div className="inline-flex">
                    <Button variant="link" className="h-auto px-1" onClick={() => onRate(c.code)}>
                      {t('settingsPages.general.currencies.editRate')}
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t('settingsPages.general.currencies.moreFor', {
                            code: ltr(c.code),
                          })}
                        >
                          <ChevronDown className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => onRevalue(c.code)}>
                          {t('settingsPages.general.currencies.revalue')}
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/settings/currencies" search={{ tab: 'rates' }}>
                            {t('settingsPages.general.currencies.rateHistory')}
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={c.inUse}
                          title={
                            c.inUse ? t('settingsPages.general.currencies.inUseTitle') : undefined
                          }
                          onSelect={() => onRemove(c.code)}
                        >
                          {t('settingsPages.general.currencies.remove')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function RateHistory({
  codes,
  home,
  staff,
  onEdit,
}: {
  codes: string[];
  home: string;
  staff: boolean;
  onEdit: (r: ExchangeRate) => void;
}) {
  const t = useT();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const [currency, setCurrency] = useState('');
  const rates = useExchangeRates(currency || undefined);
  const del = useMutation({
    mutationFn: (id: string) =>
      api<void>(`/orgs/${orgId}/exchange-rates/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['currencies', orgId] });
      toast(t('settingsPages.general.currencies.rateDeleted'));
    },
  });
  return (
    <>
      <div className="mb-3 flex items-center gap-3">
        <NativeSelect
          aria-label={t('settingsPages.general.currencies.colCurrency')}
          className="w-[200px] bg-card"
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
        >
          <option value="">{t('settingsPages.general.currencies.allCurrencies')}</option>
          {codes.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </NativeSelect>
        <p className="text-sm text-muted-foreground">
          {t('settingsPages.general.currencies.rateRule')}
        </p>
      </div>
      {rates.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : !rates.data?.length ? (
        <EmptyState
          title={t('settingsPages.general.currencies.noRatesTitle')}
          hint={t('settingsPages.general.currencies.noRatesHint')}
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table aria-label={t('settingsPages.general.currencies.tabRates')}>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[130px]">
                  {t('settingsPages.general.currencies.colDate')}
                </TableHead>
                <TableHead className="w-[100px]">
                  {t('settingsPages.general.currencies.colCurrency')}
                </TableHead>
                <TableHead className="text-end">
                  {t('settingsPages.general.currencies.colRateIn', { home })}
                </TableHead>
                <TableHead className="w-[150px]">
                  {t('settingsPages.general.currencies.colSource')}
                </TableHead>
                <TableHead className="w-[170px]">
                  {t('settingsPages.general.currencies.colChanged')}
                </TableHead>
                <TableHead className="w-[150px] text-end">
                  {t('settingsPages.general.currencies.colAction')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rates.data.map((r) => (
                <TableRow key={r.id} data-testid="rate-row">
                  <TableCell>{formatDate(r.date)}</TableCell>
                  <TableCell>{r.currency}</TableCell>
                  <TableCell className="tnum text-end">{r.rate}</TableCell>
                  <TableCell>
                    {sourceLabel(r.source)}
                    {r.provider ? ` (${r.provider})` : ''}
                  </TableCell>
                  <TableCell>{formatDateTime(r.updatedAt)}</TableCell>
                  <TableCell className="text-end">
                    {staff ? (
                      <>
                        <Button variant="link" className="h-auto px-1" onClick={() => onEdit(r)}>
                          {t('common.edit')}
                        </Button>
                        <Button
                          variant="link"
                          className="h-auto px-1"
                          onClick={() => {
                            if (
                              window.confirm(
                                t('settingsPages.general.currencies.deleteRateConfirm', {
                                  code: ltr(r.currency),
                                  date: ltr(formatDate(r.date)),
                                }),
                              )
                            )
                              del.mutate(r.id);
                          }}
                        >
                          {t('settingsPages.general.currencies.delete')}
                        </Button>
                      </>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );
}

function RevaluationHistory({ home, staff }: { home: string; staff: boolean }) {
  const t = useT();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const list = useRevaluations();
  const [undoing, setUndoing] = useState<Revaluation | null>(null);
  const undo = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api<Revaluation>(`/orgs/${orgId}/revaluations/${id}/undo`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: async (r) => {
      await qc.invalidateQueries();
      toast(
        t('settingsPages.general.currencies.revaluationUndone', {
          code: ltr(r.currency),
          date: ltr(formatDate(r.date)),
        }),
      );
      setUndoing(null);
    },
  });
  if (list.isPending) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (!list.data?.length)
    return (
      <EmptyState
        title={t('settingsPages.general.currencies.noRevaluationsTitle')}
        hint={t('settingsPages.general.currencies.noRevaluationsHint')}
      />
    );
  return (
    <>
      <div className="rounded-lg border bg-card">
        <Table aria-label={t('settingsPages.general.currencies.tabRevaluations')}>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">
                {t('settingsPages.general.currencies.colDate')}
              </TableHead>
              <TableHead className="w-[100px]">
                {t('settingsPages.general.currencies.colCurrency')}
              </TableHead>
              <TableHead className="text-end">
                {t('settingsPages.general.currencies.colRate')}
              </TableHead>
              <TableHead className="text-end">
                {t('settingsPages.general.currencies.colGainIn', { home })}
              </TableHead>
              <TableHead>{t('settingsPages.general.currencies.colEntries')}</TableHead>
              <TableHead className="w-[110px]">
                {t('settingsPages.general.currencies.colStatus')}
              </TableHead>
              <TableHead className="w-[100px] text-end">
                {t('settingsPages.general.currencies.colAction')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.data.map((r) => (
              <TableRow key={r.id} data-testid="revaluation-row">
                <TableCell>{formatDate(r.date)}</TableCell>
                <TableCell>{r.currency}</TableCell>
                <TableCell className="tnum text-end">{r.rate}</TableCell>
                <TableCell className="tnum text-end">{formatAmount(r.gain)}</TableCell>
                <TableCell className="text-sm">
                  <TextLink to={`/accounting/journal-entries/${r.entryId}`}>
                    {formatDate(r.date)}
                  </TextLink>{' '}
                  · {t('settingsPages.general.currencies.reversed')}{' '}
                  <TextLink to={`/accounting/journal-entries/${r.reversalEntryId}`}>
                    {formatDate(r.reversalDate)}
                  </TextLink>
                </TableCell>
                <TableCell>
                  {r.status === 'void' ? (
                    <span title={r.voidReason ?? undefined}>
                      {t('settingsPages.general.currencies.statusUndone')}
                    </span>
                  ) : (
                    t('settingsPages.general.currencies.statusPosted')
                  )}
                </TableCell>
                <TableCell className="text-end">
                  {staff && r.status === 'posted' ? (
                    <Button variant="link" className="h-auto px-1" onClick={() => setUndoing(r)}>
                      {t('settingsPages.general.currencies.undo')}
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <UndoDialog
        revaluation={undoing}
        pending={undo.isPending}
        error={undo.error ? undo.error.message : null}
        onCancel={() => setUndoing(null)}
        onConfirm={(reason) => undo.mutate({ id: undoing!.id, reason })}
      />
    </>
  );
}

/** Undo a revaluation: both entries reverse on their own dates; the reason is audited. */
function UndoDialog({
  revaluation,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  revaluation: Revaluation | null;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const t = useT();
  const [reason, setReason] = useState('');
  useEffect(() => setReason(''), [revaluation]);
  return (
    <Dialog open={Boolean(revaluation)} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('settingsPages.general.currencies.undoTitle', {
              code: ltr(revaluation?.currency ?? ''),
              date: ltr(revaluation ? formatDate(revaluation.date) : ''),
            })}
          </DialogTitle>
          <DialogDescription>
            {t('settingsPages.general.currencies.undoDescription')}
          </DialogDescription>
        </DialogHeader>
        {error ? <ErrorBanner>{error}</ErrorBanner> : null}
        <Field
          label={t('settingsPages.general.currencies.reason')}
          hint={t('settingsPages.general.currencies.reasonHint')}
        >
          {(id) => (
            <Textarea
              id={id}
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
            />
          )}
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            disabled={pending || !reason.trim()}
            onClick={() => onConfirm(reason.trim())}
          >
            {pending
              ? t('settingsPages.general.currencies.undoing')
              : t('settingsPages.general.currencies.undoRevaluation')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
